import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { SchemaOf } from 'yup';
import React, { useMemo } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import allCountries from 'country-region-data/data.json';
import { useRouter } from 'next/router';

import { ContentWrapper } from '../../../styles/shared';
import Input, { InputStyleType } from '../../../components/form/input/Input';
import Select, { SelectType } from '../../../components/form/select/Select';
import TextArea, {
  TextAreaStyleType,
} from '../../../components/form/text-area/TextArea';
import Field from '../../../components/form/field/Field';
import { TextSize } from '../../../models/enum';
import { ButtonStyle } from '../../../components/button/Button';
import Markdown from '../../../components/markdown/Markdown';
import { SnackbarActions, useSnackbar } from '../../../stores/snackbar';
import { FormErrorMessageTypes } from '../../../models/domain/enum';
import Radio, { RadioType } from '../../../components/form/radio/Radio';
import {
  hubspotItaliaProvince,
  hubspotItalianRegions,
} from '../../../models/domain/const';
import { capitalizeOnlyFirstLetter } from 'corporate-utils';
import { Logger } from 'corporate-utils';

import {
  ContentForm,
  Description,
  FormBody,
  FormRowGroup,
  SubmitButton,
  Title,
} from './Form.style';
import { formSubmitted } from '../../../utils/tagManager';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { UserContext } from '../../../models/interface';
import { getUseAlternativeCaptcha } from '../../../utils/captcha';

interface IProps {
  title: string;
  description: string;
  titleFontSize?: number;
  titleMobileFontSize?: number;
  titleFontWeight?: number;
  titleMobileFontWeight?: number;
  descriptionMobileFontWeight?: number;
  descriptionFontWeight?: number;
  descriptionMobileFontSize?: number;
  descriptionFontSize?: number;
}

interface ContactFormikValues {
  company: string;
  activity: string;
  firstname: string;
  lastname: string;
  email: string;
  address: string;
  city: string;
  country: string;
  profession: string;
  reason: string;
  message: string;
  profiling: string;
  marketing: string;
  region?: string;
  province?: string;
}

type ContactValidationSchema = SchemaOf<ContactFormikValues>;

const ContactForm: React.FC<{
  data: IProps;
  translations: any;
  userContext: UserContext;
}> = ({ data, translations, userContext }) => {
  const { dispatch } = useSnackbar();
  const section = 'contacts_us';
  const { executeRecaptcha } = useGoogleReCaptcha();
  const initialValues = (): ContactFormikValues => ({
    company: '',
    activity: '',
    firstname: '',
    lastname: '',
    email: '',
    address: '',
    city: '',
    country: '',
    profession: '',
    reason: '',
    message: '',
    profiling: '',
    marketing: '',
    region: '',
    province: '',
  });
  const {
    titleFontSize,
    titleMobileFontSize,
    titleFontWeight,
    titleMobileFontWeight,
    descriptionMobileFontWeight,
    descriptionFontWeight,
    descriptionMobileFontSize,
    descriptionFontSize,
  } = data || {};
  const hCaptchaRef = React.useRef<HCaptcha>(null);
  const useAlternativeCaptcha = getUseAlternativeCaptcha(userContext?.country);
  const { locale } = useRouter();

  const activities = useMemo(
    () =>
      Object.entries(translations?.activities)?.map(([key, activity]) => ({
        label: activity,
        value: key,
      })),
    [translations?.activities]
  );

  const countryList = useMemo(() => {
    const options = allCountries
      .map(
        (
          country: any
        ):
          | {
              label: string;
              value: string;
            }
          | undefined => {
          if (translations?.states?.[country.countryShortCode]) {
            return {
              label: translations?.states?.[country.countryShortCode],
              value: country.countryName,
            };
          }
        }
      )
      .filter((noUndefined: any) => !!noUndefined);

    options.unshift({
      label: translations?.pages?.workshop?.options?.stateDefault,
      value: '',
    });

    return options;
  }, [translations]);

  const regionList = useMemo(() => {
    const options = [...hubspotItalianRegions];

    options.unshift({
      label: translations?.pages?.workshop?.options?.regionDefault,
      value: '',
    });

    return options;
  }, [translations]);

  const provinceList = useMemo(() => {
    const options = hubspotItaliaProvince.map((province: any) => {
      return {
        label: capitalizeOnlyFirstLetter(province.label),
        value: province.value,
      };
    });

    options.unshift({
      label: translations?.pages?.workshop?.options?.provinceDefault,
      value: '',
    });

    return options;
  }, [translations]);

  const hubspotBodyAdapter = (values: ContactFormikValues) => {
    const result = {
      submittedAt: Date.now(),
      fields: [
        ...Object.entries(values)?.map(([key, value]) => ({
          name: translations?.pages?.contactForm?.hubspot?.[key],
          value,
        })),
      ],
    };

    if (values.country !== 'Italy') {
      result.fields.push({
        name: 'city',
        value: values.city,
      });
      result.fields = result.fields.filter(({ name }) => {
        return (
          [
            translations?.pages?.contactForm?.hubspot?.['region'],
            translations?.pages?.contactForm?.hubspot?.['province'],
          ].indexOf(name) === -1
        );
      });
    }

    return result;
  };

  const validationSchema = (): ContactValidationSchema => {
    const formatErrorMessage = (
      fieldName: string,
      errorType: FormErrorMessageTypes = FormErrorMessageTypes.REQUIRED,
      values?: any[]
    ) => {
      let message = `${fieldName} ${translations?.pages?.contactForm?.errors?.[errorType]}`;

      if (errorType === FormErrorMessageTypes.ONE_OF) {
        if (!values) throw new Error('Argument values required');
        message += `: ${values.join(',')}`;
      }

      return message;
    };

    return Yup.object({
      company: Yup.string().required(
        formatErrorMessage(
          translations?.pages?.contactForm?.form?.label?.company
        )
      ),
      activity: Yup.string().required(
        formatErrorMessage(
          translations?.pages?.contactForm?.form?.label?.activity
        )
      ),
      firstname: Yup.string().required(
        formatErrorMessage(
          translations?.pages?.contactForm?.form?.label?.firstname
        )
      ),
      lastname: Yup.string().required(
        formatErrorMessage(
          translations?.pages?.contactForm?.form?.label?.lastname
        )
      ),
      email: Yup.string()
        .required(
          formatErrorMessage(
            translations?.pages?.contactForm?.form?.label?.email
          )
        )
        .email(
          formatErrorMessage(
            translations?.pages?.contactForm?.form?.label?.email,
            FormErrorMessageTypes.EMAIL
          )
        ),
      address: Yup.string().required(
        formatErrorMessage(
          translations?.pages?.contactForm?.form?.label?.address
        )
      ),
      city: Yup.string().required(
        formatErrorMessage(translations?.pages?.contactForm?.form?.label?.city)
      ),
      country: Yup.string().required(
        formatErrorMessage(
          translations?.pages?.contactForm?.form?.label?.country
        )
      ),
      region: Yup.string().when('country', {
        is: 'Italy',
        then: Yup.string().required(
          formatErrorMessage(
            translations?.pages?.contactForm?.form?.label?.region
          )
        ),
      }),
      province: Yup.string().when('country', {
        is: 'Italy',
        then: Yup.string().required(
          formatErrorMessage(
            translations?.pages?.contactForm?.form?.label?.province
          )
        ),
      }),
      profession: Yup.string().required(
        formatErrorMessage(
          translations?.pages?.contactForm?.form?.label?.profession
        )
      ),
      reason: Yup.string().required(
        formatErrorMessage(
          translations?.pages?.contactForm?.form?.label?.reason
        )
      ),
      message: Yup.string().required(
        formatErrorMessage(
          translations?.pages?.contactForm?.form?.label?.message
        )
      ),
      profiling: Yup.string().required(
        formatErrorMessage(
          translations?.pages?.contactForm?.form?.label?.profiling
        )
      ),
      marketing: Yup.string().required(
        formatErrorMessage(
          translations?.pages?.contactForm?.form?.label?.marketing
        )
      ),
    });
  };

  const submitForm = async (
    values: ContactFormikValues,
    { resetForm }: FormikHelpers<ContactFormikValues>
  ) => {
    try {
      let token;

      if (useAlternativeCaptcha) {
        token = await hCaptchaRef?.current
          ?.execute({ async: true })
          .then(({ response }) => {
            return response;
          })
          .catch((err) => {
            console.error(err);
          });
      } else {
        token = (await executeRecaptcha?.('contact')) || '';
      }

      const headers = new Headers();
      headers.set('Content-Type', 'application/json');
      const resp = await fetch('/api/contact-hubspot', {
        headers,
        method: 'POST',
        body: JSON.stringify({
          locale,
          captchaToken: {
            token,
            alternativeCaptcha: useAlternativeCaptcha,
          },
          action: 'contact',
          data: hubspotBodyAdapter(values),
        }),
      });
      const isSuccess = resp.status === 200;
      const data: {
        result: {
          message?: string;
          inlineMessage?: string;
        };
        formGuid?: string;
      } = await resp.json();
      dispatch({
        type: SnackbarActions.SHOW,
        success: isSuccess,
        message: isSuccess
          ? data?.result?.inlineMessage?.replace(/(<([^>]+)>)/gi, '')
          : data?.result?.message || translations?.pages?.errors?.generic,
      });

      if (isSuccess) {
        data?.formGuid && formSubmitted(data?.formGuid);
        resetForm();
      }
    } catch (error) {
      const err = error as Error;
      Logger.error(err);
      dispatch({
        type: SnackbarActions.SHOW,
        success: false,
        message: translations?.pages?.errors?.generic,
      });
    }
  };

  const professionOptions = useMemo(
    () => [
      {
        label: translations?.pages?.contactForm?.options?.reseller,
        value: 'rivenditore',
      },
      {
        label: translations?.pages?.contactForm?.options?.user,
        value: 'utlizzatore',
      },
      {
        label: translations?.pages?.contactForm?.options?.other,
        value: 'altro',
      },
    ],
    [
      translations?.pages?.contactForm?.options?.reseller,
      translations?.pages?.contactForm?.options?.user,
      translations?.pages?.contactForm?.options?.other,
    ]
  );

  const reasonOptions = useMemo(
    () => [
      {
        label: translations?.pages?.contactForm?.options?.priceList,
        value: 'Listino Prezzi',
      },
      {
        label:
          translations?.pages?.contactForm?.options?.informationAboutProducts,
        value: 'Informazioni sul prodotto',
      },
      {
        label: translations?.pages?.contactForm?.options?.technicalAssistance,
        value: 'Assistenza tecnica',
      },
      {
        label: translations?.pages?.contactForm?.options?.areaDealer,
        value: 'Rivenditore di zona',
      },
      {
        label: translations?.pages?.contactForm?.options?.partnership,
        value: 'Collaborazione',
      },
      {
        label: translations?.pages?.contactForm?.options?.other,
        value: 'Altro',
      },
    ],
    [
      translations?.pages?.contactForm?.options?.priceList,
      translations?.pages?.contactForm?.options?.informationAboutProducts,
      translations?.pages?.contactForm?.options?.technicalAssistance,
      translations?.pages?.contactForm?.options?.areaDealer,
      translations?.pages?.contactForm?.options?.partnership,
      translations?.pages?.contactForm?.options?.other,
    ]
  );

  const profilingOptions = useMemo(
    () => [
      {
        label: translations?.pages?.contactForm?.options?.yes,
        value: 'profilazione_consenso',
      },
      {
        label: translations?.pages?.contactForm?.options?.no,
        value: 'profilazione_rifiuto',
      },
    ],
    [
      translations?.pages?.contactForm?.options?.no,
      translations?.pages?.contactForm?.options?.yes,
    ]
  );

  const marketingOptions = useMemo(
    () => [
      {
        label: translations?.pages?.contactForm?.options?.yes,
        value: 'marketing_consenso',
      },
      {
        label: translations?.pages?.contactForm?.options?.no,
        value: 'marketing_rifiuto',
      },
    ],
    [
      translations?.pages?.contactForm?.options?.no,
      translations?.pages?.contactForm?.options?.yes,
    ]
  );

  return (
    <ContentWrapper>
      <Formik
        initialValues={initialValues()}
        validationSchema={(): ContactValidationSchema => validationSchema()}
        validateOnChange={true}
        validateOnBlur={true}
        onSubmit={submitForm}
      >
        {({ values }) => {
          return (
            <Form>
              <ContentForm>
                {data?.title && (
                  <Title
                    fontSize={titleFontSize}
                    fontMobileSize={titleMobileFontSize}
                    fontWeight={titleFontWeight}
                    fontMobileWeight={titleMobileFontWeight}
                  >
                    <Markdown>{data?.title ?? ''}</Markdown>
                  </Title>
                )}
                <Description
                  fontSize={descriptionFontSize}
                  fontMobileSize={descriptionMobileFontSize}
                  fontWeight={descriptionFontWeight}
                  fontMobileWeight={descriptionMobileFontWeight}
                >
                  {data?.description ?? ''}
                </Description>
                <FormBody>
                  <FormRowGroup>
                    <Field
                      label={
                        translations?.pages?.contactForm?.form?.label?.company
                      }
                      name="company"
                      section={section}
                      placeholder={
                        translations?.pages?.contactForm?.form
                          ?.labelsPlaceholders?.company
                      }
                      styleType={InputStyleType.workshop}
                      component={Input}
                    />
                    <Field
                      label={
                        translations?.pages?.contactForm?.form?.label?.activity
                      }
                      name="activity"
                      section={section}
                      selectType={SelectType.workshop}
                      placeholder={
                        translations?.pages?.contactForm?.form
                          ?.labelsPlaceholders?.activity
                      }
                      options={activities}
                      textSize={TextSize.small}
                      isPlaceholderValue={!values.activity}
                      component={Select}
                    />
                  </FormRowGroup>
                  <FormRowGroup>
                    <Field
                      label={
                        translations?.pages?.contactForm?.form?.label?.firstname
                      }
                      name="firstname"
                      section={section}
                      placeholder={
                        translations?.pages?.contactForm?.form
                          ?.labelsPlaceholders?.firstname
                      }
                      styleType={InputStyleType.workshop}
                      component={Input}
                    />
                    <Field
                      label={
                        translations?.pages?.contactForm?.form?.label?.lastname
                      }
                      name="lastname"
                      section={section}
                      placeholder={
                        translations?.pages?.contactForm?.form
                          ?.labelsPlaceholders?.lastname
                      }
                      styleType={InputStyleType.workshop}
                      component={Input}
                    />
                  </FormRowGroup>
                  <FormRowGroup>
                    <Field
                      label={
                        translations?.pages?.contactForm?.form?.label?.email
                      }
                      name="email"
                      section={section}
                      placeholder={
                        translations?.pages?.contactForm?.form
                          ?.labelsPlaceholders?.email
                      }
                      type="email"
                      styleType={InputStyleType.workshop}
                      component={Input}
                    />
                    <Field
                      label={
                        translations?.pages?.contactForm?.form?.label?.address
                      }
                      name="address"
                      section={section}
                      placeholder={
                        translations?.pages?.contactForm?.form
                          ?.labelsPlaceholders?.address
                      }
                      type="address"
                      styleType={InputStyleType.workshop}
                      component={Input}
                    />
                  </FormRowGroup>
                  <FormRowGroup>
                    <Field
                      label={
                        translations?.pages?.contactForm?.form?.label?.city
                      }
                      name="city"
                      section={section}
                      placeholder={
                        translations?.pages?.contactForm?.form
                          ?.labelsPlaceholders?.city
                      }
                      styleType={InputStyleType.workshop}
                      component={Input}
                    />
                    <Field
                      label={
                        translations?.pages?.contactForm?.form?.label?.country
                      }
                      name="country"
                      selectType={SelectType.workshop}
                      section={section}
                      placeholder={
                        translations?.pages?.contactForm?.form
                          ?.labelsPlaceholders?.country
                      }
                      options={countryList}
                      isPlaceholderValue={!values.country}
                      textSize={TextSize.small}
                      component={Select}
                    />
                  </FormRowGroup>

                  {values.country === 'Italy' && (
                    <FormRowGroup>
                      <Field
                        label={
                          translations?.pages?.contactForm?.form?.label?.region
                        }
                        name="region"
                        section={section}
                        selectType={SelectType.workshop}
                        placeholder={
                          translations?.pages?.contactForm?.form
                            ?.labelsPlaceholders?.region
                        }
                        options={regionList}
                        isPlaceholderValue={!values.region}
                        textSize={TextSize.small}
                        component={Select}
                      />
                      <Field
                        label={
                          translations?.pages?.contactForm?.form?.label
                            ?.province
                        }
                        name="province"
                        selectType={SelectType.workshop}
                        section={section}
                        placeholder={
                          translations?.pages?.contactForm?.form
                            ?.labelsPlaceholders?.province
                        }
                        options={provinceList}
                        isPlaceholderValue={!values.province}
                        textSize={TextSize.small}
                        component={Select}
                      />
                    </FormRowGroup>
                  )}

                  <FormRowGroup>
                    <Field
                      label={
                        translations?.pages?.contactForm?.form?.label
                          ?.profession
                      }
                      name="profession"
                      section={section}
                      radioType={RadioType.workshop}
                      options={professionOptions}
                      textSize={TextSize.workshop}
                      component={Radio}
                    />
                  </FormRowGroup>
                  <FormRowGroup>
                    <Field
                      label={
                        translations?.pages?.contactForm?.form?.label?.reason
                      }
                      name="reason"
                      section={section}
                      selectType={SelectType.workshop}
                      placeholder={
                        translations?.pages?.contactForm?.form
                          ?.labelsPlaceholders?.reason
                      }
                      options={reasonOptions}
                      textSize={TextSize.small}
                      isPlaceholderValue={!values.reason}
                      component={Select}
                    />
                  </FormRowGroup>
                  <FormRowGroup>
                    <Field
                      label={
                        translations?.pages?.contactForm?.form?.label?.message
                      }
                      name="message"
                      section={section}
                      placeholder={
                        translations?.pages?.contactForm?.form
                          ?.labelsPlaceholders?.message
                      }
                      styleType={TextAreaStyleType.workshop}
                      rows="8"
                      component={TextArea}
                    />
                  </FormRowGroup>
                  <FormRowGroup>
                    <Field
                      label={
                        translations?.pages?.contactForm?.form?.label?.marketing
                      }
                      subText={
                        translations?.pages?.contactForm?.form
                          ?.labelsPlaceholders?.marketing
                      }
                      name="marketing"
                      section={section}
                      radioType={RadioType.workshop}
                      options={marketingOptions}
                      textSize={TextSize.workshop}
                      component={Radio}
                    />
                  </FormRowGroup>
                  <FormRowGroup>
                    <Field
                      label={
                        translations?.pages?.contactForm?.form?.label?.profiling
                      }
                      subText={
                        translations?.pages?.contactForm?.form
                          ?.labelsPlaceholders?.profiling
                      }
                      name="profiling"
                      section={section}
                      radioType={RadioType.workshop}
                      options={profilingOptions}
                      textSize={TextSize.workshop}
                      component={Radio}
                    />
                  </FormRowGroup>
                  <FormRowGroup>
                    <SubmitButton
                      text={translations?.pages?.contactForm?.submit}
                      styleBtn={ButtonStyle.contactFormPage}
                      type="submit"
                    />
                  </FormRowGroup>
                  {useAlternativeCaptcha && (
                    <HCaptcha
                      sitekey={process.env.NEXT_PUBLIC_H_CAPTCHA_SITE_KEY || ''}
                      size="invisible"
                      ref={hCaptchaRef}
                    />
                  )}
                </FormBody>
              </ContentForm>
            </Form>
          );
        }}
      </Formik>
    </ContentWrapper>
  );
};

export default ContactForm;
